import { useEffect, useMemo } from 'react';

import {
	classifyWithMatrixFilter,
	classifyWithSimpleFilter,
	hexToRgba,
} from '../hexagonColorFilter/matrixFilter/matrixClassification';
import { buildClassificationStyle, buildClassColorMap } from './buildClassificationStyle';
import { defaultStyle } from '../../layers/singleImage/SingleImageFootprintsLayers';

/**
 * Classifies enriched DSP features based on the current filter configuration.
 * Pure classification logic extracted for testability.
 *
 * @param {Object} params
 * @param {Array<Feature>} params.features - Enriched DSP features (with values_.data.metadata)
 * @param {Object} params.filter - Current filter configuration (matrix or simple)
 * @returns {Object} classificationResults - Per-class results with counts, areas, feature refs
 */
export function classifyDspFeatures({ features, filter }) {
	const results = {};

	// Initialize results for each class
	filter.classes.forEach(cls => {
		results[cls.id] = {
			...cls,
			colorObj: hexToRgba(cls.color, cls.opacity ?? 0.7),
			features: [],
			count: 0,
			totalArea: 0,
			areaPercentage: '0.0',
		};
	});

	// Add special categories
	results.below_minimum = {
		id: 'below_minimum',
		label: 'Below Minimum',
		color: '#808080',
		colorObj: { r: 128, g: 128, b: 128, a: 0.5 },
		features: [],
		count: 0,
		totalArea: 0,
		areaPercentage: '0.0',
	};
	results.unclassified = {
		id: 'unclassified',
		label: 'Unclassified',
		color: '#646464',
		colorObj: { r: 100, g: 100, b: 100, a: 0.3 },
		features: [],
		count: 0,
		totalArea: 0,
		areaPercentage: '0.0',
	};

	// Classify each feature
	features.forEach(feature => {
		const meta = feature.get('data')?.metadata;
		if (!meta) {
			results.unclassified.features.push(feature);
			return;
		}

		// Use appropriate classification function based on filter type
		const classId =
			filter.type === 'simple'
				? classifyWithSimpleFilter(meta, filter)
				: classifyWithMatrixFilter(meta, filter);

		if (classId && results[classId]) {
			results[classId].features.push(feature);
		} else {
			results.unclassified.features.push(feature);
		}
	});

	// Calculate statistics — use actual per-feature areas
	const totalFeatures = features.length;
	Object.keys(results).forEach(classId => {
		const r = results[classId];
		r.count = r.features.length;
		r.totalArea = r.features.reduce((sum, f) => sum + (f.get('area') || 0), 0);
		r.areaPercentage = totalFeatures > 0 ? ((r.count / totalFeatures) * 100).toFixed(1) : '0.0';
	});

	return results;
}

/**
 * React hook that classifies enriched DSP features and applies WebGL styles
 * to the shared footprints layer.
 *
 * Instead of per-feature setStyle() (Canvas-only), this hook:
 * 1. Sets a `classId` property on each feature (WebGL reads this via expressions)
 * 2. Builds a WebGL style expression mapping classId → color
 * 3. Applies the style to the layer via layer.setStyle()
 *
 * @param {Object} params
 * @param {Array<Feature>} params.features - Enriched DSP features (with values_.data.metadata)
 * @param {Object} params.filter - Current filter configuration (matrix or simple)
 * @param {Function} params.setFilterBoxes - Callback to update parent's filter boxes state
 * @param {import('ol/layer/WebGLVector').default|null} params.layer - The WebGL footprints layer
 * @returns {Object} classificationResults - Per-class results with counts, areas, feature refs
 */
export default function useDspClassification({ features, filter, setFilterBoxes, layer }) {
	// Classify features and compute results
	const classificationResults = useMemo(
		() => classifyDspFeatures({ features, filter }),
		[features, filter]
	);

	// Apply classId to features and update the layer's WebGL style expression.
	useEffect(() => {
		if (!features.length || !layer) return;

		// Set classId property on each feature — WebGL style expression reads this
		Object.keys(classificationResults).forEach(classId => {
			const result = classificationResults[classId];
			result.features.forEach(feature => {
				feature.set('classId', classId);
			});
		});

		// Build and apply WebGL style expression
		const classColorMap = buildClassColorMap(classificationResults, filter.classes);
		const style = buildClassificationStyle(classColorMap);
		layer.setStyle(style);

		// Update filter boxes for summary/export
		const filterBoxes = filter.classes.map(cls => {
			const r = classificationResults[cls.id];
			return {
				label: cls.label,
				color: r.colorObj,
				multiplier: cls.multiplier || 1,
				matchedFeatures: r.count,
				totalArea: r.totalArea,
				areaPercentage: r.areaPercentage,
				totalCalculation: r.totalArea * (cls.multiplier || 1),
			};
		});

		// Include special categories when they have features
		const belowMin = classificationResults.below_minimum;
		if (belowMin?.count > 0) {
			filterBoxes.push({
				label: belowMin.label,
				color: belowMin.colorObj,
				multiplier: 0,
				matchedFeatures: belowMin.count,
				totalArea: belowMin.totalArea,
				areaPercentage: belowMin.areaPercentage,
				totalCalculation: 0,
			});
		}

		const unclassified = classificationResults.unclassified;
		if (unclassified?.count > 0) {
			filterBoxes.push({
				label: unclassified.label,
				color: unclassified.colorObj,
				multiplier: 0,
				matchedFeatures: unclassified.count,
				totalArea: unclassified.totalArea,
				areaPercentage: unclassified.areaPercentage,
				totalCalculation: 0,
			});
		}

		setFilterBoxes(filterBoxes);

		// Cleanup: revert style and clear classId when this effect re-runs or unmounts
		return () => {
			features.forEach(feature => {
				feature.set('classId', undefined);
			});
			layer.setStyle(defaultStyle);
		};
	}, [classificationResults, features, setFilterBoxes, filter.classes, layer]);

	return classificationResults;
}
